import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import { Alert, Badge, Card, CardBody, CardHeader, Skeleton } from '../../components/ui'

/* Faithful port of anansi/app/templates/admin/role_catalog.html + its view
 * admin/routes.py:role_catalog(). This is the STATUTORY role catalog
 * (role_catalog_service.py, RTDB role_catalog/<key>) - drives the
 * Governance Coverage panel on org detail pages. Deliberately separate
 * from the Role Builder (Roles.tsx/RoleForm.tsx), a different system
 * (role_service.py, custom_roles) despite the similar name. */

const LICENCE_TYPES = ['artisanal', 'small_scale', 'exploration', 'processing', 'dealer', 'trader'] as const
const SCOPES: { value: string; label: string }[] = [
  { value: 'mining', label: 'Mining (statutory)' },
  { value: 'operational', label: 'Operational (industry practice)' },
  { value: 'coop_governance', label: 'Cooperative governance' },
]
const REQ_SHORT: Record<string, string> = { mandatory: 'M', recommended: 'R', not_applicable: '–' }
const REQ_TONE: Record<string, 'danger' | 'warning' | 'neutral'> = {
  mandatory: 'danger', recommended: 'warning', not_applicable: 'neutral',
}

interface CatalogRole {
  key: string
  title: string
  scope: string
  statute_ref?: string
  description?: string
  conditional_note?: string
  applies_to_licence_types: Record<string, string>
  applies_to_org_types: string[]
  min_count: number
  active: boolean
}
interface CatalogResponse { ok: boolean; roles: CatalogRole[] }

export default function AdminRoleCatalog() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'role-catalog'],
    queryFn: () => api.get<CatalogResponse>('/role-catalog?include_inactive=1'),
  })

  const rows = data?.roles ?? []
  const byScope = (scope: string) => rows.filter((r) => r.scope === scope)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Statutory Role Catalog</h1>
          <p className="page-head__sub">
            Governance and operational positions required by law or industry practice - drives the
            compliance coverage every organisation is measured against.
          </p>
        </div>
      </div>

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardBody>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            <Badge tone="danger">M</Badge> Mandatory - required for that licence type.{' '}
            <Badge tone="warning">R</Badge> Recommended, not required.{' '}
            <Badge tone="neutral">–</Badge> Not applicable.
          </p>
        </CardBody>
      </Card>

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading ? (
        <div className="stack"><Skeleton h={120} /><Skeleton h={120} /></div>
      ) : (
        <div className="stack" style={{ gap: 'var(--sp-6)' }}>
          {SCOPES.map((s) => {
            const scopeRows = byScope(s.value)
            if (scopeRows.length === 0) return null
            return (
              <Card key={s.value}>
                <CardHeader title={s.label} />
                <CardBody flush>
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Role</th>
                          {LICENCE_TYPES.map((lt) => <th key={lt}>{titleCase(lt)}</th>)}
                          <th>Min</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {scopeRows.map((r) => (
                          <tr key={r.key}>
                            <td>
                              <div style={{ fontWeight: 550 }}>
                                {r.title}
                                {!r.active && <span style={{ marginLeft: 6 }}><Badge tone="neutral">Inactive</Badge></span>}
                              </div>
                              {r.statute_ref && (
                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{r.statute_ref}</div>
                              )}
                              {r.conditional_note && (
                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--warning)' }}>⚠ {r.conditional_note}</div>
                              )}
                              <div style={{ marginTop: 4 }}>
                                {(r.applies_to_org_types ?? []).map((t) => (
                                  <Badge key={t} tone="info">{titleCase(t)}</Badge>
                                ))}
                              </div>
                            </td>
                            {LICENCE_TYPES.map((lt) => {
                              const req = r.applies_to_licence_types?.[lt] || 'not_applicable'
                              return (
                                <td key={lt} style={{ textAlign: 'center' }}>
                                  <Badge tone={REQ_TONE[req]}>{REQ_SHORT[req]}</Badge>
                                </td>
                              )
                            })}
                            <td>{r.min_count}</td>
                            <td style={{ textAlign: 'right' }}>
                              <Link to={`/role-catalog/${r.key}`}>Edit</Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            )
          })}
        </div>
      )}

      <Card style={{ marginTop: 'var(--sp-6)' }}>
        <CardHeader title="Bulk import / export" />
        <CardBody>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--sp-3)' }}>
            CSV import/export stays on the original admin pages for now.
          </p>
          <div className="row" style={{ gap: 'var(--sp-3)' }}>
            <a className="btn btn--secondary btn--sm" href="/admin/role-catalog/template.csv">Download template</a>
            <a className="btn btn--secondary btn--sm" href="/admin/role-catalog">Import CSV →</a>
          </div>
        </CardBody>
      </Card>
    </>
  )
}
